import type { CSSProperties } from 'react';
import { useSyncExternalStore } from 'react';

/** Shared recharts styling so every Insikter chart follows the theme tokens (and dark mode). */

/** Compact axis tick for whole-krona values: 950 → "950", 12 400 → "12k", 1 250 000 → "1,3M" */
export function formatCompactKr(kr: number) {
  const abs = Math.abs(kr);
  if (abs >= 1_000_000)
    return `${(kr / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1000) return `${Math.round(kr / 1000)}k`;
  return `${Math.round(kr)}`;
}

/** Whole kronor for tooltips, e.g. "12 400 kr" */
export function formatWholeKr(kr: unknown) {
  return `${Math.round(Number(kr)).toLocaleString('sv-SE')} kr`;
}

const SM_QUERY = '(min-width: 640px)';

function subscribeSm(onChange: () => void) {
  const mql = window.matchMedia(SM_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/** True at Tailwind's `sm` breakpoint and up; sizes chart axes for narrow screens */
export function useIsSmUp() {
  return useSyncExternalStore(
    subscribeSm,
    () => window.matchMedia(SM_QUERY).matches,
    () => true,
  );
}

export const CHART_MARGIN = { top: 8, right: 4, bottom: 0, left: 0 };

export const axisProps = {
  tick: { fontSize: 11, fill: 'var(--muted-foreground)' },
  stroke: 'var(--border)',
  tickLine: false,
  axisLine: false,
} as const;

export const gridProps = {
  stroke: 'var(--border)',
  strokeDasharray: '3 3',
} as const;

export const tooltipContentStyle: CSSProperties = {
  background: 'var(--popover)',
  color: 'var(--popover-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  fontSize: 13,
  boxShadow: 'none',
};

export const tooltipItemStyle: CSSProperties = { color: 'var(--popover-foreground)' };

export const tooltipLabelStyle: CSSProperties = {
  color: 'var(--muted-foreground)',
  marginBottom: 2,
};
